import { PlantCommentButton, PlantStatusButton } from "@/shared/plant/PlantOrderControls";
import { useTransState } from "../useTransState";
import ApiService from "@/api/ApiService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { MaterialOrder, ProjectJob } from "@/components/planning/Project";
import { Box, CircularProgress, Tooltip } from "@mui/material";

import { GridColDef, DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";

import {
  CheckBox,
  CheckBoxOutlineBlank,
  CheckCircle,
  Close,
  DeleteForever,
  DraftsOutlined,
  Edit,
  Email,
  EmailOutlined,
  Event,
  HowToReg,
  ManageSearch,
  MyLocation,
  NewReleases,
  RemoveCircle,
  RsvpTwoTone,
  Thunderstorm,
  Warehouse,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { createArticleText, createDeliveryText } from "@/components/planning/editors/MaterialTab";
import useRights from "@/shared/api/useRights";
import { Fragment } from "react/jsx-runtime";
import MachineTransportWrap from "@/components/machinetransport/MachineTransportWrap";
import { usePersistentDataGrid } from "@/shared/hooks/usePersistentDataGrid";
import { useMemo, useState } from "react";
import ExternalOrderHistoryDialog from "../dialogs/ExternalOrderHistoryDialog";
import OrderHistoryDialog, { xBFormatter } from "../dialogs/OrderHistoryDialog";
import OrderLocationDialog from "../dialogs/OrderLocationDialog";
import { usePersistantStore } from "@/stores/global";
import { OrderPosition } from "@/shared/types";
import { Info } from "lucide-react";
import { DirectNewExternalMaterialEditor } from "./DirectNewExternalMaterialEditor";
import { NewExternalOrder } from "./AddNewExternalDialog";
import { plantVisibleRows } from "./plantOrderVisibility";
import type { TransportOrderStatus } from "@/stores/transportOrderStatus";

interface ResourceTag {
  id: number;
  name: string;
  type: "HUMAN" | "RESOURCE" | "CREW";
  calendarType: "NONE" | "CALENDAR_ONLY" | "WISHABLE" | "REQUESTABLE";
}

export interface StaffMember {
  id: number;
  name: string;
  tags: ResourceTag[];
}

export interface Crew {
  id: number;
  name: string;
}

export interface Resource {
  id: number;
  name: string;
  tags: ResourceTag[];
  number: string;
}

interface ResourceBooking {
  id: number;
  crew: Crew | null;
  isLeader: boolean;
  resource: Resource | null;
  staffMember: StaffMember | null;
  order: ResourceOrder;
  type: "CREW" | "RESOURCE" | "HUMAN";
}

interface ResourceOrder {
  id: number;
  amount: number;
  tag: ResourceTag | null;
  comment: string;
  type: "CREW" | "RESOURCE" | "HUMAN";
  job: number | null;
  phase: number | null;
  deleted: boolean;
  references: (number | null)[];
  exceptions: string[];
}

export interface WithOrders {
  materialOrders: MaterialOrder[];
  resourceOrders: {
    resourceBookings: ResourceBooking[];
    resourceOrders: ResourceOrder[];
  };
}

export interface ManualOrder {
  id: number;
  type: "manual";
  info: string;
  logistics: string;
  article: string;
  amount: string;
  contact: string;
  project: string;
  number: string;
  firstLoad: string;
  date: string;
  plant: number;
  plantName: string;
}

export type MainOrder = {
  type: "auto";
  materialOrder: MaterialOrder & {
    createdAt: string;
    firstLoad: string;
    firstLoadTrans: string;
    article: MaterialOrder["article"] & { productGroup: string; productSubGroup: string };
    plantComment: string;
    firstUnloadTrans: string;
    position: null | (OrderPosition & { au: string });
  };
  firstLoad: string;
  phase: {
    name: string;
    project: {
      name: string;
      number: string;
      manager: string;
    };
  };
} & ProjectJob;

type PossibleOrder = ManualOrder | MainOrder | (NewExternalOrder & { type: "external" });

export type TransState = TransportOrderStatus;

export const transStates: Record<
  TransState,
  {
    icon: typeof Email;
    next: TransState;
    title: string;
    iconColorOnWhite: string;
    iconColorOnBackground: string;
    backgroundColor: string;
    needsAction: boolean;
  }
> = {
  OPEN: {
    icon: Email,
    next: "SENT",
    title: "Neue Bestellung",
    iconColorOnWhite: "darkblue",
    iconColorOnBackground: "white",
    backgroundColor: "white",
    needsAction: true,
  },
  RETRACTED: {
    icon: Close,
    next: "RETRACTED",
    title: "Zurückgezogen",
    iconColorOnWhite: "#777",
    iconColorOnBackground: "white",
    backgroundColor: "white",
    needsAction: false,
  },
  SENT: {
    icon: DraftsOutlined,
    next: "ACCEPTED",
    title: "Gesehen",
    iconColorOnWhite: "green",
    iconColorOnBackground: "darkgreen",
    backgroundColor: "lightgreen",
    needsAction: false,
  },
  ACCEPTED: {
    icon: CheckCircle,
    next: "ACCEPTED",
    title: "Akzeptiert",
    iconColorOnWhite: "green",
    iconColorOnBackground: "darkgreen",
    backgroundColor: "lightgreen",
    needsAction: false,
  },
  CHANGED: {
    icon: NewReleases,
    next: "SENT",
    title: "Geändert",
    iconColorOnWhite: "orange",
    iconColorOnBackground: "black",
    backgroundColor: "orange",
    needsAction: true,
  },
  MOVED: {
    icon: Event,
    next: "SENT",
    title: "Verschoben",
    iconColorOnWhite: "orange",
    iconColorOnBackground: "black",
    backgroundColor: "orange",
    needsAction: true,
  },
  STORNO: {
    icon: DeleteForever,
    next: "DELETED",
    title: "Storniert",
    iconColorOnWhite: "red",
    iconColorOnBackground: "white",
    backgroundColor: "darkred",
    needsAction: true,
  },
  DELETED: {
    icon: RemoveCircle,
    next: "STORNO",
    title: "Gelöscht",
    iconColorOnWhite: "#777",
    iconColorOnBackground: "darkred",
    backgroundColor: "red",
    needsAction: false,
  },
};

type PlantViewModal = ["HISTORY" | "LOCATION" | "EXTERNAL_HISTORY", number];

function getColumns(modalSetter: (modal: PlantViewModal) => void, withPlant: boolean, writePlantId: number, transReadOnly: boolean): GridColDef[] {
  const cells: GridColDef<PossibleOrder>[] = [];

  if (withPlant) {
    cells.push({
      headerName: "Werk",
      flex: 1,
      field: "plant",
      valueGetter: (value, row) => {
        return row.type === "external"
          ? row.plant.name
          : row.type === "manual"
            ? row.plantName
            : !row.materialOrder.article
              ? ""
              : row.materialOrder.article.plantName;
      },
    });
  }
  return cells.concat([
    {
      field: "date",
      headerName: "Datum",
      width: 150,
      type: "date",
      display: "flex",
      valueGetter: (value, row) => dayjs(row.date, "YYYY-MM-DD").toDate(),
      renderCell: ({ row }) => (
        <Fragment>
          {row.type === "auto" && !!row.badWeather && !row.badWeather.resetAt ? (
            <Tooltip title={row.badWeather.comment}>
              <Thunderstorm fontSize="small" sx={{ mr: 1 }} />
            </Tooltip>
          ) : null}
          {dayjs(row.date, "YYYY-MM-DD").format("ddd, DD.MM.YYYY") + (row.type === "auto" && row?.duration === "HALF" ? " - halbtags" : "")}
        </Fragment>
      ),
    },
    {
      field: "loadtime",
      headerName: "LKW an Waage",
      width: 100,
      display: "flex",
      renderCell: ({ row }) => (
        <Fragment>
          {row.type !== "external" && row.type !== "manual" ? <span className="transtime">{row.materialOrder.firstLoadTrans}</span> : null}
          <span
            className={
              "plantime " +
              (row.type === "external" || row.type === "manual" || !row.materialOrder.firstLoadTrans
                ? "notrans"
                : row.materialOrder.firstLoadTrans === row.firstLoad || !row.firstLoad
                  ? "sametrans"
                  : "difftrans")
            }
          >
            {((ts) => (!ts ? "-" : !ts.startsWith("x") ? ts : ts === "xAM" ? "vormittags" : ts === "xPM" ? "nachmittags" : "???"))(
              row.type === "manual" || row.type === "external" ? row.firstLoad : row.materialOrder.firstLoad,
            )}
          </span>
        </Fragment>
      ),
      valueGetter: (_, row) =>
        row.type !== "external" && row.type !== "manual" && !!row.materialOrder.firstLoadTrans
          ? row.materialOrder.firstLoadTrans
          : ((ts) => (!ts ? "-" : !ts.startsWith("x") ? ts : ts === "xAM" ? "vormittags" : ts === "xPM" ? "nachmittags" : "???"))(
              row.type === "manual" || row.type === "external" ? row.firstLoad : row.materialOrder.firstLoad,
            ),
    },
    {
      field: "time",
      headerName: "LKW an BS",
      width: 100,
      display: "flex",
      renderCell: ({ row }) => (
        <Fragment>
          {row.type !== "manual" && row.type !== "external" ? <span className="transtime">{row.materialOrder.firstUnloadTrans}</span> : null}
          <span
            className={
              "plantime " +
              (row.type === "manual" || row.type === "external" || !row.materialOrder.firstUnloadTrans
                ? "notrans"
                : row.materialOrder.firstUnloadTrans === row.materialOrder.firstUnload || !row.firstUnload
                  ? "sametrans"
                  : "difftrans")
            }
          >
            {((ts) =>
              !ts
                ? "-"
                : !ts.startsWith("x")
                  ? ts
                  : ts.startsWith("xB")
                    ? xBFormatter(ts, "BS", false)
                    : ts === "xAM"
                      ? "vormittags"
                      : ts === "xPM"
                        ? "nachmittags"
                        : "???")(row.type === "manual" || row.type === "external" ? false : row.materialOrder.firstUnload)}
          </span>
        </Fragment>
      ),
      valueGetter: (_, row) =>
        row.type !== "external" && row.type !== "manual" && !!row.materialOrder.firstUnloadTrans
          ? row.materialOrder.firstUnloadTrans
          : ((ts) =>
              !ts
                ? "anschließend"
                : !ts.startsWith("x")
                  ? ts
                  : ts.startsWith("xB")
                    ? xBFormatter(ts, "BS", false)
                    : ts === "xAM"
                      ? "vormittags"
                      : ts === "xPM"
                        ? "nachmittags"
                        : "???")(row.type === "manual" || row.type === "external" ? "-" : row.materialOrder.firstUnload),
    },
    {
      field: "au",
      headerName: "AU-Nr.",
      flex: 1,
      valueGetter: (_, row) =>
        row.type === "external" ? (row?.position?.supplierOrder.number ?? "-") : row.type === "manual" ? "-" : row.materialOrder?.position?.au || "-",
    },
    {
      field: "costbase",
      headerName: "Kostenstelle",
      width: 100,
      valueGetter: (_, row) => (row.type === "external" ? (row?.number ?? "-") : row.type === "manual" ? row.number : row.phase.project.number),
    },
    {
      field: "project",
      headerName: "Baustelle",
      flex: 1,
      valueGetter: (_, row) => (row.type === "external" ? row.project : row.type === "manual" ? row.project : row.phase.project.name),
    },
    {
      field: "manager",
      headerName: "Bauleiter",
      flex: 1,
      valueGetter: (_, row) =>
        row.type === "external"
          ? row.customer.name
          : row.type === "manual"
            ? "-"
            : !!row.phase.project.manager
              ? row.phase.project.manager.name + ", " + row.phase.project.manager.firstName
              : "",
    },
    {
      field: "crew",
      headerName: "Kolonne",
      flex: 1,
      valueGetter: (_, row) => (row.type !== "auto" ? row.contact : Object.values(row.crews).join("; ")),
    },
    {
      field: "amount",
      headerName: "Menge",
      type: "number",
      display: "flex",
      align: "right",
      width: 85,
      renderCell: ({ row, value, formattedValue }) =>
        value == null ? null : row.type !== "auto" ? (
          <strong>{formattedValue} {row.type === "manual" ? "t" : row.article.unit}</strong>
        ) : (
          createArticleText(row.materialOrder, false, false, "amount")
        ),
      valueGetter: (_, row) => {
        // Mehrartikelbestellungen bleiben vollständig in der Artikelspalte.
        if (
          row.type === "auto" &&
          (!row.materialOrder.article || row.materialOrder.additionalArticles.some((article) => !article.deleted && article.article))
        ) {
          return null;
        }
        const amount = row.type === "auto" ? row.materialOrder.amount : row.amount;
        if (amount == null || String(amount).trim() === "") return null;
        const numericAmount = Number(String(amount).replace(",", "."));
        return Number.isFinite(numericAmount) ? numericAmount : null;
      },
      valueFormatter: (value: number | null) => value == null ? "" : value.toString().replace(".", ","),
      sortable: false,
    },
    {
      field: "artikel",
      headerName: "Artikel",
      display: "flex",
      flex: 2,
      renderCell: ({ row }) =>
        row.type === "external" ? (
          <Fragment>
            {row.article.name} ({row.article.number})
          </Fragment>
        ) : row.type === "manual" ? (
          <Fragment>{row.article}</Fragment>
        ) : (
          createArticleText(row.materialOrder, false, false, "article")
        ),
      valueGetter: (_, row) =>
        row.type === "external"
          ? `${row.article.name} (${row.article.number})`
          : row.type === "manual"
            ? `${row.article}`
            : createArticleText(row.materialOrder, false, true, "article"),
      sortable: false,
    },
    {
      field: "productgroup",
      headerName: "Produktgruppe",
      flex: 1,
      valueGetter: (_, row) =>
        row.type === "manual" ? "extern" : row.type === "external" ? row.article.productGroup : row.materialOrder?.article?.productGroup,
    },
    {
      field: "productsubgroup",
      headerName: "Produktuntergruppe",
      flex: 1,
      valueGetter: (_, row) =>
        row.type === "manual" ? "extern" : row.type === "external" ? row.article.productSubGroup : row.materialOrder?.article?.productSubGroup,
    },
    {
      field: "delivery",
      headerName: "Logistik",
      flex: 2,
      valueGetter: (_, row) =>
        row.type !== "auto" ? row.logistics : createDeliveryText(row.materialOrder.transportInformation, row.materialOrder.takt),
      sortable: false,
    },
    {
      field: "begleitschein",
      headerName: "Begleitschein?",
      flex: 1,
      valueGetter: (_, row) => (row.type !== "auto" ? "-" : row.materialOrder?.begleitschein ? "Ja" : "Nein"),
    },
    {
      field: "comment",
      flex: 1,
      headerName: "Weitere Informationen",
      valueGetter: (_, row) =>
        row.type === "external"
          ? row.comment
          : row.type === "manual"
            ? row.info
            : [
                !row.materialOrder.position && !!row.materialOrder.article && !row.materialOrder.article.bauhof ? "Unbepreister Artikel!" : "",
                row.materialOrder.easyTarget === "LAGER" ? "Anlieferung zum Lagerplatz!" : "",
                row.materialOrder.comment,
              ]
                .filter((x) => x.length > 0)
                .join(" - "),
    },
    {
      field: "state",
      width: 100,
      headerName: "Bestellstatus",
      valueGetter: (_, row) =>
        row.type !== "auto"
          ? "-"
          : (row.status === "PLANNING"
              ? "in Planung"
              : row.status === "RAW"
                ? "grobgeplant"
                : row.status === "READY"
                  ? "grobgeplant"
                  : "feingeplant") + (!!row.materialOrder.orderSentBy ? ", bestellt" : ""),
    },
    {
      field: "creation",
      headerName: "Erstelldatum",
      type: "date",
      flex: 1,
      valueGetter: (value, row) =>
        row.type === "external"
          ? dayjs.utc(row.createdAt).toDate()
          : row.type === "auto"
            ? dayjs.utc(row.materialOrder?.createdAt).toDate()
            : new Date(),
      valueFormatter: (value, row) => {
        return row.type === "external"
          ? dayjs.utc(row.createdAt).format("ddd, DD.MM.YYYY HH:mm:ss")
          : row.type === "manual"
            ? "-"
            : dayjs.utc(row.materialOrder?.createdAt).format("ddd, DD.MM.YYYY HH:mm:ss");
      },
    },
    {
      field: "transportState",
      display: "flex",
      width: 80,
      headerName: "trans",
      renderCell: (params) =>
        params.row.type !== "auto" ? null : (
          <TransState readOnly={transReadOnly} id={params.row.materialOrder.id} state={params.row.materialOrder.transportStatus} />
        ),
      valueGetter: (_v, row) => row?.materialOrder?.transportStatus,
    },
    {
      field: "plantState",
      display: "flex",
      width: 80,
      headerName: "Werk",
      renderCell: (params) =>
        params.row.type === "external" ? (
          writePlantId !== -1 && writePlantId !== params.row.plant.id ? null : (
            <DirectNewExternalMaterialEditor allowedPlant={writePlantId} order={params.row} />
          )
        ) : params.row.type === "manual" ? (
          writePlantId !== -1 && writePlantId !== params.row.plant ? null : null
        ) : (
          <Box sx={{ display: "flex", flex: 1, justifyContent: "space-between" }}>
            <PlantState
              readOnly={writePlantId !== -1 && params.row.materialOrder?.article?.plantId !== writePlantId}
              isBauhof={!!params.row.materialOrder.article && !!params.row.materialOrder.article.bauhof}
              id={params.row.materialOrder.id}
              state={params.row.materialOrder.plantStatus}
            />
            {!!params.row.materialOrder.article && !!params.row.materialOrder.article.bauhof ? null : (
              <PlantComment readOnly={writePlantId !== -1 && params.row.materialOrder?.article?.plantId !== writePlantId} materialOrder={params.row.materialOrder} />
            )}
          </Box>
        ),
      valueGetter: (_v, row) => row?.materialOrder?.plantStatus,
    },
    {
      field: "info",
      display: "flex",
      width: 80,
      headerName: "Historie",
      renderCell: ({ row }) => {
        if (row.type !== "auto" && row.type !== "external") return null;
        const modal: PlantViewModal = row.type === "external" ? ["EXTERNAL_HISTORY", row.id] : ["HISTORY", row.materialOrder.id];
        return (
          <Tooltip title={"Änderungshistorie"}>
            <ManageSearch
              sx={{ cursor: "pointer", "&:hover": { color: "#666" } }}
              onClick={() => modalSetter(modal)}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "target",
      display: "flex",
      width: 80,
      headerName: "Ziel",
      renderCell: (params) =>
        params.row.type !== "auto" ? null : (
          <Tooltip title={"Zielort anzeigen"}>
            <MyLocation
              sx={{ cursor: "pointer", "&:hover": { color: "#666" } }}
              onClick={() => modalSetter(["LOCATION", params.row.materialOrder.id])}
            />
          </Tooltip>
        ),
    },
  ]);
}

const PlantComment = ({ materialOrder, readOnly }: { materialOrder: any; readOnly: boolean }) => {
  const queryClient = useQueryClient();
  return <PlantCommentButton comment={materialOrder.plantComment ?? ""} critical={materialOrder.critical ?? false} readOnly={readOnly} save={async (data) => {
    await ApiService.setPlantStatus()({ ...data, id: materialOrder.id });
    void queryClient.invalidateQueries({ queryKey: ["getMaterialCalendar"] });
  }} />;
};

type WerkState = "OPEN" | "RETRACTED" | "READ" | "PACKED" | "CHANGED" | "MOVED" | "STORNO" | "DELETED";

export const states: Record<WerkState, [typeof Email, WerkState, string, string]> = {
  OPEN: [Email, "READ", "Neue Bestellung", "darkblue"],
  READ: [DraftsOutlined, "OPEN", "Gesehen", "green"],
  PACKED: [Warehouse, "READ", "Gepackt", "grey"],
  CHANGED: [NewReleases, "READ", "Geändert", "orange"],
  RETRACTED: [Close, "RETRACTED", "Zurückgezogen", "#777"],
  MOVED: [Event, "READ", "Verschoben", "orange"],
  STORNO: [DeleteForever, "DELETED", "Storniert", "red"],
  DELETED: [RemoveCircle, "STORNO", "Gelöscht", "#777"],
};

export const PlantState = ({ state, id, readOnly, isBauhof }: { state: WerkState; id: number; readOnly: boolean; isBauhof: boolean }) => {
  const queryClient = useQueryClient();
  return <PlantStatusButton status={state} nextStatus={states[state][1]} icon={states[state][0]} color={states[state][3]} label={states[state][2]} disabled={readOnly} save={async (next) => {
    try {
      await ApiService.setPlantOrderStatus(id)(state === "READ" && isBauhof ? "PACKED" : next);
    } finally {
      void queryClient.invalidateQueries({ queryKey: ["getMaterialCalendar"] });
    }
  }} />;
};

export const TransState = ({ state, id, readOnly }: { state: TransState; id: number; readOnly: boolean }) => {
  const { status: cstate, mutation: changeMutation } = useTransState(id, state);

  const Icon = transStates[cstate].icon;

  return changeMutation.isPending ? (
    <CircularProgress />
  ) : (
    <Tooltip title={changeMutation.isError ? "Transportstatus konnte nicht geändert werden. Bitte erneut versuchen." : transStates[cstate].title}>
      <Icon
        sx={{
          cursor: "pointer",
          color: changeMutation.isError ? "error.main" : transStates[cstate].iconColorOnWhite,
          "&:hover": { color: "#666" },
        }}
        onClick={readOnly ? undefined : () => changeMutation.mutate(transStates[cstate].next)}
      />
    </Tooltip>
  );
};

export default function MixingPlantView({ reference, history }: { reference: string | null; history: boolean }) {
  const rights = useRights();
  const region = usePersistantStore((r) => r.region);
  const apiRef = useGridApiRef();
  const [modalOpen, setModalOpen] = useState<PlantViewModal | null>(null);
  const withPlant = reference === null || "" + reference === "0" || (!!reference && ("" + reference).includes(","));
  const writePlantId = "PLANT_ADMIN" in rights ? -1 : !("PLANT" in rights) ? 0 : parseInt(rights["PLANT"]);
  const transReadOnly = !("TRANSPORTDISPO" in rights);
  const columns = useMemo(() => getColumns(setModalOpen, withPlant, writePlantId, transReadOnly), [withPlant, writePlantId, transReadOnly]);

  const { gridProps } = usePersistentDataGrid(
    "mixingplants",
    columns,
    {
      columnVisibilityModel: {
        comment: false,
        crew: false,
        delivery: false,
        info: false,
        plantState: false,
        state: false,
        target: false,
        time: false,
        transportState: false,
      },
    },
  );

  const materialQuery = useQuery<any[], AxiosResponse>({
    queryKey: ["getMaterialCalendar", "" + reference, "" + region, history ? "history" : "none"],
    queryFn: ApiService.getCalendar("MATERIAL", reference, history ? "history" : "none", region),
  });
  const hasPlantRight = "PLANT" in rights;
  const visibleRows = useMemo(() => plantVisibleRows(materialQuery.data ?? [], hasPlantRight), [materialQuery.data, hasPlantRight]);

  return (
    <div className="calListWrapper">
      <OrderHistoryDialog orderId={!!modalOpen && modalOpen[0] === "HISTORY" ? modalOpen[1] : 0} close={() => setModalOpen(null)} />
      <ExternalOrderHistoryDialog orderId={!!modalOpen && modalOpen[0] === "EXTERNAL_HISTORY" ? modalOpen[1] : 0} close={() => setModalOpen(null)} />
      <OrderLocationDialog orderId={!!modalOpen && modalOpen[0] === "LOCATION" ? modalOpen[1] : 0} close={() => setModalOpen(null)} />
      {materialQuery.isLoading ? (
        <CircularProgress />
      ) : materialQuery.isLoadingError ? (
        "Error"
      ) : (
        <DataGridPro
          showToolbar
          disableRowSelectionOnClick
          getRowHeight={({ model }) => 25 + (model.materialOrder?.additionalArticles?.length || 0) * 14}
          rows={visibleRows}
          apiRef={apiRef}
          getRowId={(row) => (row.type === "external" || row.type === "manual" ? row.id : row.materialOrder.id)}
          loading={materialQuery.isLoading}
          getRowClassName={(params: any) => {
            const currentRowId = params.id;

            // Step 1: Get sorted row IDs (includes all rows, even filtered-out ones)
            const sortedRowIds = apiRef.current?.getSortedRowIds() || [];

            // Step 2: Filter only currently visible rows using `getRowIndexRelativeToVisibleRows`
            const visibleSortedRows = sortedRowIds.filter((rowId) => {
              return apiRef.current?.getRowIndexRelativeToVisibleRows(rowId) !== -1; // Row must be in visible rows
            });

            // Step 3: Find index of current row in visible sorted rows
            const currentIndex = visibleSortedRows.indexOf(currentRowId);
            const currentRow = apiRef.current?.getRow(currentRowId);

            // Step 4: Check if the row is deleted (applies even to first row)
            const isBadWeather = !!currentRow?.badWeather && !currentRow.badWeather.resetAt ? " order-badWeather" : "";
            const isDeleted = currentRow?.materialOrder?.deleted ? "order-deleted" : "";

            if (currentIndex === 0) {
              return isDeleted + isBadWeather; // First row doesn't compare with previous row but can be deleted
            }

            // Step 5: Get previous row ID and row data
            const previousRowId = visibleSortedRows[currentIndex - 1];
            const previousRow = apiRef.current?.getRow(previousRowId);

            // Ensure both rows exist and have a valid `date` property
            if (!currentRow || !previousRow || !currentRow.date || !previousRow.date) {
              return isDeleted;
            }

            // Step 6: Compare dates
            const currentDate = dayjs(currentRow.date, "YYYY-MM-DD");
            const previousDate = dayjs(previousRow.date, "YYYY-MM-DD");

            const isDateDifferent = currentDate.isSame(previousDate, "day") ? "" : "date-different-row";

            // Step 7: Combine classes
            return `${isDateDifferent} ${isDeleted} ${isBadWeather}`.trim(); // Trim ensures no extra spaces if one class is empty
          }}
          {...gridProps}
        />
      )}
    </div>
  );
}
